import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { playIntro } from "./smokeIntro";
import { setIntroPhase, waitForFonts } from "../../lib/intro";

// The lines are written in Kalam; a short wait for it avoids a font swap in the middle of their fade.
const FONTS = ["1em Kalam"];
const FONT_WAIT_MS = 600;
const MODIFIER_KEYS = new Set(["Shift", "Control", "Alt", "Meta"]);
const LINES = ["Zapalte svíčku.", "Vyslovte její jméno…"];

/** La Muerte's sugar skull (BrandMark) as strokes for the smoke to draw. */
const SmokeSkull = () => (
  <svg className="intro-skull" viewBox="0 0 40 44">
    <path className="draw" d="M20 2C9 2 3 10 3 19c0 6 3 10 6 13v9h22v-9c3-3 6-7 6-13C37 10 31 2 20 2z" />
    <circle className="draw lit eye-left" cx="13" cy="20" r="5" />
    <circle className="draw lit eye-right" cx="27" cy="20" r="5" />
    <path className="draw" d="M20 25l-3 5h6z" />
    <path className="draw lit star" d="M20 5l1.5 3 3 .3-2.2 2 .6 3-2.9-1.6L17.1 13l.6-3-2.2-2 3-.3z" />
    <path className="draw" d="M13 36v5M17 36v5M23 36v5M27 36v5" />
    <g className="pupils">
      <circle cx="13" cy="20" r="2" />
      <circle cx="27" cy="20" r="2" />
    </g>
  </svg>
);

/**
 * Makes the overlay's siblings (the page) inert, so focus, pointers and screen readers cannot reach controls hidden
 * behind the curtain. Returns a function that makes them live again.
 */
const makeSiblingsInert = (element) => {
  const siblings = [...element.parentElement.children].filter(
    (sibling) => sibling !== element && !sibling.hasAttribute("inert")
  );
  siblings.forEach((sibling) => sibling.setAttribute("inert", ""));
  return () => siblings.forEach((sibling) => sibling.removeAttribute("inert"));
};

/**
 * Full-screen intro for a page load on the home route: a candle is lit and blown out, its smoke draws La Muerte's
 * skull, marigold petals pour down and the page is revealed from the top. A click, a key or the wheel skips it.
 */
const IntroOverlay = ({ onFinish }) => {
  const rootRef = useRef(null);
  const introRef = useRef(null);
  const isSkippedRef = useRef(false);
  const releasePageRef = useRef(() => {});

  // Before the first paint, so the hero never shows ahead of its entrance; the page stays inert until revealed.
  useLayoutEffect(() => {
    setIntroPhase("playing");
    releasePageRef.current = makeSiblingsInert(rootRef.current);
    return () => {
      releasePageRef.current();
      setIntroPhase(null);
    };
  }, []);

  // Straight to the page: used when skipping before the choreography starts, or when it cannot run at all.
  const showPage = useCallback(() => {
    releasePageRef.current();
    setIntroPhase(null);
    onFinish();
  }, [onFinish]);

  const skip = useCallback(() => {
    if (isSkippedRef.current) return;
    isSkippedRef.current = true;
    if (!introRef.current) {
      showPage();
      return;
    }
    releasePageRef.current();
    introRef.current.skip();
  }, [showPage]);

  useEffect(() => {
    let isCancelled = false;
    const start = () => {
      if (isCancelled || isSkippedRef.current) return;
      rootRef.current.classList.add("is-playing");
      introRef.current = playIntro(rootRef.current, {
        onEnter: () => setIntroPhase("entering"),
        onReveal: () => releasePageRef.current(),
        onDone: () => setIntroPhase(null),
        onFinish,
      });
    };
    waitForFonts(FONTS, FONT_WAIT_MS)
      .then(start)
      .catch((error) => {
        console.error("Intro unavailable, showing the page:", error);
        if (!isCancelled) showPage();
      });
    const onKeyDown = (event) => {
      if (!MODIFIER_KEYS.has(event.key)) skip();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      isCancelled = true;
      window.removeEventListener("keydown", onKeyDown);
      introRef.current?.destroy();
      introRef.current = null;
    };
  }, [onFinish, showPage, skip]);

  return (
    <div ref={rootRef} className="intro-overlay" onPointerDown={skip} onWheel={skip}>
      <div className="intro-curtain" aria-hidden="true">
        <div className="intro-scene">
          <div className="intro-light" />
          <div className="intro-candle" />
          <div className="intro-ember" />
          <div className="intro-flame">
            <i />
          </div>
          <svg className="intro-smoke" viewBox="-4 -12 8 12">
            <path d="M0 0C-1.2-2 1.2-4 0-6S-1.5-9.5.6-12" />
            <path d="M0 0C.9-1.8-1.1-3.8.2-5.6S1.6-8.8-.4-11.4" />
          </svg>
          <SmokeSkull />
          <div className="intro-lines">
            {LINES.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>
      </div>
      <div className="intro-petals" aria-hidden="true" />
      <button type="button" className="intro-skip" onClick={skip}>
        Přeskočit intro
      </button>
    </div>
  );
};

export default IntroOverlay;
